import React, { useState } from 'react';
import { useApiKeys, useGenerateApiKey, useUpdateApiKey, useDeleteApiKey } from '@/hooks/useApiKeys';
import { useOrganizations } from '@/hooks/useOrganizations';
import { ApiKey } from '@/services/apiKeys.service';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Plus, Copy, Eye, EyeOff, AlertCircle, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { ApiKeyDataTable } from './ApiKeyDataTable';
import { useTranslation } from 'react-i18next';

export const DeveloperSection: React.FC = () => {
  const { t } = useTranslation();
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string>('');

  const { data: organizationsData } = useOrganizations();
  const { data: apiKeysData, isLoading } = useApiKeys(selectedOrganizationId);
  const generateMutation = useGenerateApiKey();
  const updateMutation = useUpdateApiKey();
  const deleteMutation = useDeleteApiKey();

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [selectedApiKey, setSelectedApiKey] = useState<ApiKey | null>(null);
  const [newApiKeyResult, setNewApiKeyResult] = useState<string | null>(null);
  const [showNewApiKey, setShowNewApiKey] = useState(false);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    organizationId: '',
    environment: 'live' as 'live' | 'test',
    permissions: {
      read: true,
      write: false
    }
  });

  const organizations = organizationsData?.data || [];
  const apiKeys = apiKeysData?.data || [];

  const handleCreateApiKey = async () => {
    try {
      const result = await generateMutation.mutateAsync(formData);
      setNewApiKeyResult(result.data.api_key);
      setShowNewApiKey(true);
      toast.success(t('common:x4.apiKeyCreated', 'API Key erstellt'));
      setFormData({
        name: '',
        description: '',
        organizationId: '',
        environment: 'live',
        permissions: { read: true, write: false }
      });
    } catch (error) {
      console.error('Failed to create API key:', error);
      toast.error(t('common:x4.apiKeyCreateFailed', 'Fehler beim Erstellen des API Keys'));
    }
  };

  const handleCloseDialog = () => {
    setIsCreateDialogOpen(false);
    setNewApiKeyResult(null);
    setShowNewApiKey(false);
    setSelectedApiKey(null);
  };

  const handleUpdateApiKey = async () => {
    if (!selectedApiKey) return;

    try {
      await updateMutation.mutateAsync({
        id: selectedApiKey.id,
        data: {
          name: formData.name,
          description: formData.description,
          permissions: formData.permissions,
          isActive: selectedApiKey.is_active,
          organizationId: selectedApiKey.organization_id
        }
      });
      handleCloseDialog();
      toast.success(t('common:x4.apiKeyUpdated', 'API Key aktualisiert'));
    } catch (error) {
      console.error('Failed to update API key:', error);
      toast.error(t('common:x4.apiKeyUpdateFailed', 'Fehler beim Aktualisieren des API Keys'));
    }
  };

  const handleDeleteApiKey = async (apiKey: ApiKey) => {
    if (confirm(`Möchten Sie den API Key "${apiKey.name}" wirklich löschen?`)) {
      try {
        await deleteMutation.mutateAsync({
          id: apiKey.id,
          organizationId: apiKey.organization_id
        });
        toast.success(t('common:x4.apiKeyDeleted', 'API Key gelöscht'));
      } catch (error) {
        console.error('Failed to delete API key:', error);
        toast.error(t('common:x4.apiKeyDeleteFailed', 'Fehler beim Löschen des API Keys'));
      }
    }
  };

  const handleToggleActive = async (apiKey: ApiKey) => {
    try {
      await updateMutation.mutateAsync({
        id: apiKey.id,
        data: {
          isActive: !apiKey.is_active,
          organizationId: apiKey.organization_id
        }
      });
      toast.success(`API Key ${apiKey.is_active ? 'deaktiviert' : 'aktiviert'}`);
    } catch (error) {
      console.error('Failed to toggle API key status:', error);
      toast.error(t('common:x1.statusChangeFailed', 'Status konnte nicht geändert werden'));
    }
  };

  const openEditDialog = (apiKey: ApiKey) => {
    setSelectedApiKey(apiKey);
    setFormData({
      name: apiKey.name,
      description: apiKey.description || '',
      organizationId: apiKey.organization_id,
      environment: apiKey.prefix === 'sk_test' ? 'test' : 'live',
      permissions: apiKey.permissions
    });
    setIsCreateDialogOpen(true);
  };

  const copyToClipboard = async (text: string, keyId: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(keyId);
      setTimeout(() => setCopiedKey(null), 2000);
      toast.success(t('common:x4.copiedToClipboard', 'In die Zwischenablage kopiert'));
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="space-y-6">
      <Card className="border border-white/70 bg-white/70 backdrop-blur-xl ring-1 ring-black/5">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <CardTitle className="text-base font-semibold text-slate-900">{t('common:x4.developerApi', 'Developer API')}</CardTitle>
              <span className="text-xs text-slate-400 font-normal ml-2">({apiKeys.length} Keys)</span>
            </div>
            <CardDescription className="text-sm text-slate-500">
              Verwalten Sie API Keys für externe Integrationen und programmatischen Zugriff.
            </CardDescription>
          </div>
          <div className="flex items-center gap-3">
            <Select
              value={selectedOrganizationId || "all"}
              onValueChange={(value) => setSelectedOrganizationId(value === "all" ? "" : value)}
            >
              <SelectTrigger className="h-9 w-[180px] rounded-lg text-xs bg-slate-50 border-slate-200">
                <SelectValue placeholder={t('ticket:ph.selectOrg', 'Organisation wählen')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('common:state.allOrgs', 'Alle Organisationen')}</SelectItem>
                {organizations.map((org: any) => (
                  <SelectItem key={org.id} value={org.id}>
                    {org.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Dialog open={isCreateDialogOpen} onOpenChange={(open) => !open && handleCloseDialog()}>
              <DialogTrigger asChild>
                <Button
                  onClick={() => {
                    setFormData({
                      name: '',
                      description: '',
                      organizationId: selectedOrganizationId || '',
                      environment: 'live',
                      permissions: { read: true, write: false }
                    });
                    setSelectedApiKey(null);
                    setNewApiKeyResult(null);
                    setIsCreateDialogOpen(true);
                  }}
                  className="h-9 gap-2 bg-slate-900 text-white hover:bg-slate-800"
                >
                  <Plus className="h-4 w-4" />
                  {t('common:x4.createApiKey', 'API Key erstellen')}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[800px] p-0 overflow-hidden border-0 rounded-2xl max-h-[85vh] flex flex-col">
                <div className="flex flex-1 overflow-hidden">
                  {/* Sidebar */}
                  <div className="bg-slate-50 border-r border-slate-100 p-6 flex flex-col gap-4 w-[240px] flex-shrink-0">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm font-semibold text-slate-900">
                          {newApiKeyResult ? 'Key erstellt' : selectedApiKey ? 'Key bearbeiten' : 'Neuer API Key'}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 leading-relaxed">
                        {newApiKeyResult 
                          ? 'WICHTIG: Kopieren Sie den Key jetzt. Er wird später nicht mehr angezeigt.'
                          : 'Erstellen Sie sichere Zugriffsschlüssel für Ihre Anwendungen.'}
                      </p>
                    </div>
                    
                    <div className="mt-auto rounded-xl bg-white p-4 border border-slate-100">
                      <span className="text-[10px] uppercase font-bold text-slate-400 block mb-1">{t('common:x4.environments', 'Umgebungen')}</span>
                      <ul className="space-y-2">
                        <li className="flex items-center gap-2 text-xs text-slate-600">
                          <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                          <span>{t('common:x4.liveProduction', 'Live: Produktion')}</span>
                        </li>
                        <li className="flex items-center gap-2 text-xs text-slate-600">
                          <div className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                          <span>{t('common:x4.testSandbox', 'Test: Sandbox')}</span>
                        </li>
                      </ul>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex flex-col flex-1 bg-white min-w-0">
                    <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center flex-shrink-0">
                      <DialogTitle className="text-base font-semibold text-slate-900">
                        {t('common:x4.apiKeyConfig', 'API Key Konfiguration')}
                      </DialogTitle>
                    </div>
                    
                    <div className="flex-1 overflow-y-auto p-6">
                      {newApiKeyResult ? (
                        <div className="space-y-6">
                          <div className="rounded-lg bg-amber-50 border border-amber-200 p-4">
                            <div className="flex items-start gap-3">
                              <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                              <div>
                                <p className="text-sm font-semibold text-amber-900">{t('common:x4.securityWarning', 'Sicherheitswarnung')}</p>
                                <p className="text-xs text-amber-800 mt-1">
                                  Dieser API Key wird nur einmalig angezeigt. Bitte speichern Sie ihn an einem sicheren Ort.
                                </p>
                              </div>
                            </div>
                          </div>

                          <div className="space-y-2">
                            <Label className="text-xs font-medium text-slate-700">{t('common:x4.yourApiKey', 'Ihr API Key')}</Label>
                            <div className="flex items-center gap-2">
                              <code className="flex-1 text-xs bg-slate-900 text-green-400 rounded-lg px-3 py-2.5 font-mono break-all leading-relaxed">
                                {showNewApiKey ? newApiKeyResult : '•'.repeat(60)}
                              </code>
                              <div className="flex flex-col gap-1">
                                <Button
                                  variant="outline"
                                  size="icon"
                                  onClick={() => setShowNewApiKey(!showNewApiKey)}
                                  className="h-8 w-8 hover:bg-slate-100"
                                >
                                  {showNewApiKey ? (
                                    <EyeOff className="h-3.5 w-3.5 text-slate-600" />
                                  ) : (
                                    <Eye className="h-3.5 w-3.5 text-slate-600" />
                                  )}
                                </Button>
                                <Button
                                  variant="outline"
                                  size="icon"
                                  onClick={() => copyToClipboard(newApiKeyResult, 'new-key')}
                                  className="h-8 w-8 hover:bg-slate-100"
                                >
                                  {copiedKey === 'new-key' ? (
                                    <CheckCircle className="h-3.5 w-3.5 text-green-600" />
                                  ) : (
                                    <Copy className="h-3.5 w-3.5 text-slate-600" />
                                  )}
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-6 max-w-lg">
                          <div className="space-y-2">
                            <Label htmlFor="name" className="text-xs font-medium text-slate-700">{t('common:x1.nameReq', 'Name *')}</Label>
                            <Input
                              id="name"
                              placeholder={t('common:x4.egShopifyProd', 'z.B. Shopify Integration Prod')}
                              value={formData.name}
                              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                              className="h-9 border-slate-200 focus-visible:ring-slate-200"
                            />
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="description" className="text-xs font-medium text-slate-700">{t('common:field.description', 'Beschreibung')}</Label>
                            <Textarea
                              id="description"
                              placeholder={t('common:x4.keyUsedFor', 'Wofür wird dieser Key verwendet?')}
                              value={formData.description}
                              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                              className="min-h-[80px] text-xs resize-none border-slate-200 focus-visible:ring-slate-200"
                            />
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label htmlFor="organization" className="text-xs font-medium text-slate-700">{t('common:x1.orgReq', 'Organisation *')}</Label>
                              <Select
                                value={formData.organizationId}
                                onValueChange={(value) => setFormData({ ...formData, organizationId: value })}
                                disabled={!!selectedApiKey}
                              >
                                <SelectTrigger className="h-9 border-slate-200 focus:ring-slate-200">
                                  <SelectValue placeholder={t('common:x4.choose', 'Wählen')} />
                                </SelectTrigger>
                                <SelectContent>
                                  {organizations.map((org: any) => (
                                    <SelectItem key={org.id} value={org.id}>
                                      {org.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            <div className="space-y-2">
                              <Label htmlFor="environment" className="text-xs font-medium text-slate-700">{t('common:x4.environment', 'Umgebung')}</Label>
                              <Select
                                value={formData.environment}
                                onValueChange={(value: 'live' | 'test') => setFormData({ ...formData, environment: value })}
                                disabled={!!selectedApiKey}
                              >
                                <SelectTrigger className="h-9 border-slate-200 focus:ring-slate-200">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="live">{t('common:x4.liveProd', 'Live (Produktion)')}</SelectItem>
                                  <SelectItem value="test">{t('common:x4.testSb', 'Test (Sandbox)')}</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>

                          <div className="space-y-3 pt-2">
                            <Label className="text-xs font-medium text-slate-700">{t('common:field.permissions', 'Berechtigungen')}</Label>
                            <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-3 space-y-3">
                              <div className="flex items-center justify-between">
                                <div>
                                  <div className="text-xs font-medium text-slate-900">{t('common:x4.readAccess', 'Read Access')}</div>
                                  <div className="text-[10px] text-slate-500">{t('common:x4.readDataDesc', 'Daten lesen und abfragen')}</div>
                                </div>
                                <Switch
                                  checked={formData.permissions.read}
                                  onCheckedChange={(checked) =>
                                    setFormData({ ...formData, permissions: { ...formData.permissions, read: checked } })
                                  }
                                  className="scale-75 data-[state=checked]:bg-[#0F99FD]"
                                />
                              </div>
                              <div className="flex items-center justify-between border-t border-slate-200/60 pt-3">
                                <div>
                                  <div className="text-xs font-medium text-slate-900">{t('common:x4.writeAccess', 'Write Access')}</div>
                                  <div className="text-[10px] text-slate-500">{t('common:x4.writeDataDesc', 'Daten erstellen und ändern')}</div>
                                </div>
                                <Switch
                                  checked={formData.permissions.write}
                                  onCheckedChange={(checked) =>
                                    setFormData({ ...formData, permissions: { ...formData.permissions, write: checked } })
                                  }
                                  className="scale-75 data-[state=checked]:bg-[#0F99FD]"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <DialogFooter className="p-4 border-t border-slate-100 bg-slate-50/50 flex justify-end gap-2 flex-shrink-0">
                      <Button
                        variant="outline"
                        onClick={handleCloseDialog}
                        className="h-9 rounded-lg border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-white"
                      >
                        {newApiKeyResult ? 'Schließen' : 'Abbrechen'}
                      </Button>
                      {!newApiKeyResult && (
                        <Button
                          onClick={selectedApiKey ? handleUpdateApiKey : handleCreateApiKey}
                          disabled={!formData.name.trim() || !formData.organizationId || generateMutation.isPending || updateMutation.isPending}
                          className="h-9 rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50"
                        >
                          {generateMutation.isPending || updateMutation.isPending ? (
                            <>
                              <Loader2 className="h-3 w-3 mr-2 animate-spin" />
                              {t('common:state.savingShort', 'Speichert...')}
                            </>
                          ) : (
                            selectedApiKey ? 'Aktualisieren' : 'Erstellen'
                          )}
                        </Button>
                      )}
                    </DialogFooter>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          <ApiKeyDataTable 
            apiKeys={apiKeys}
            isLoading={isLoading}
            onEdit={openEditDialog}
            onDelete={handleDeleteApiKey}
            onToggleActive={handleToggleActive}
            isTogglePending={updateMutation.isPending}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default DeveloperSection;
